import { useState, useEffect, createContext, useContext } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { readAffiliateParams } from "@/lib/affiliateTracking";

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

const META_PIXEL_ID = "1012759761594637";
const OAUTH_COMPLETE_REGISTRATION_FLAG = "ha_oauth_complete_registration_fired";

// Fires the Meta CompleteRegistration event on-page, directly at the moment a
// new account is confirmed created — not on a downstream redirect URL, which
// can change independently of whether someone actually registered.
export function fireMetaCompleteRegistration(email: string, displayName?: string) {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;

  const [firstName, ...rest] = (displayName ?? "").trim().split(/\s+/);
  const lastName = rest.join(" ");

  window.fbq("init", META_PIXEL_ID, {
    em: email.trim().toLowerCase(),
    ...(firstName && { fn: firstName.toLowerCase() }),
    ...(lastName && { ln: lastName.toLowerCase() }),
  });

  // Reuse the same click_id we send to trigger the click/id postback so this
  // Pixel event can be deduplicated/joined against that attribution record.
  // Falls back to a random UUID for organic signups with no affiliate click_id.
  const eventID = readAffiliateParams()?.click_id ?? crypto.randomUUID();

  window.fbq(
    "track",
    "CompleteRegistration",
    { content_name: "Stack account", status: true },
    { eventID },
  );
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName?: string, extraMetadata?: Record<string, string>) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log("[useAuth] onAuthStateChange:", event, "hasSession:", !!newSession);
      // Only clear user on explicit sign-out. Transient events (e.g. a failed
      // TOKEN_REFRESHED under rate-limit) can briefly arrive with a null
      // session and would otherwise bounce the user to /auth mid-session.
      if (event === "SIGNED_OUT") {
        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }
      if (newSession) {
        setSession(newSession);
        setUser(newSession.user);

        // OAuth signups (e.g. "Sign up with Google") never call signUp() —
        // the browser redirects away and back, so the new-account moment has
        // to be caught here instead. A first-ever session has created_at and
        // last_sign_in_at set together; any later login moves last_sign_in_at
        // forward while created_at stays fixed.
        const { user: oauthUser } = newSession;
        const isFirstEverSession = oauthUser.created_at === oauthUser.last_sign_in_at;
        const isOAuthIdentity = (oauthUser.app_metadata?.provider ?? "email") !== "email";
        if (isFirstEverSession && isOAuthIdentity) {
          let alreadyFired = false;
          try {
            alreadyFired = sessionStorage.getItem(OAUTH_COMPLETE_REGISTRATION_FLAG) === oauthUser.id;
          } catch (_) {
            // sessionStorage unavailable (e.g. private browsing) — proceed as not-yet-fired
          }
          if (!alreadyFired && oauthUser.email) {
            fireMetaCompleteRegistration(oauthUser.email, oauthUser.user_metadata?.full_name ?? oauthUser.user_metadata?.name);
            try {
              sessionStorage.setItem(OAUTH_COMPLETE_REGISTRATION_FLAG, oauthUser.id);
            } catch (_) {
              // sessionStorage unavailable — dedup flag just won't persist
            }
          }
        }
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log("[useAuth] initial getSession hasSession:", !!session);
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    displayName?: string,
    extraMetadata?: Record<string, string>,
  ) => {
    // Centralized first-touch attribution. See src/lib/attribution.ts.
    const { getAttributionPayload } = await import("@/lib/attribution");
    const attribution = getAttributionPayload();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin,
        data: { display_name: displayName, ...(extraMetadata ?? {}), ...attribution },
      },
    });

    // Supabase returns success (no error) even when the email is already
    // registered, to prevent user enumeration — identities is empty in that
    // case, so only fire on a genuinely new account.
    if (!error && (data.user?.identities?.length ?? 0) > 0) {
      fireMetaCompleteRegistration(email, displayName);
    }

    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
